import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { AlertTriangle, Ban, CheckCircle2, Download, FileText, Landmark, Loader2, Receipt, RefreshCcw, Send, Settings2 } from 'lucide-react';
import api from '../../utils/api';
import { money } from '../../utils/format';
import { useConfirm } from '../../components/ConfirmDialog';
import { Dialog, EmptyState, ErrorState, TableSkeleton } from '../../components/ui';
import { Notice } from '../Financials/financeKit';
import { dmy, errText, todayIso } from '../Settings/payRatesKit';

// Timesheets > Contractor bills (owner spec "Pay Rates, Sleepover, Kilometres and Xero Workflow v1.2",
// section 8), for companies on Pay Rates. The same pay run as the SC module:
//   1 Contractor bills   one bill per contractor per pay period, built from the approved pay lines and
//                        sent to Xero; contractors that fail a check are listed in Exceptions
//   2 Payment file       an ABA file for bills in Xero: check, generate, download, mark paid, void
// Opening the screen reads the bills' status back from Xero: a bill paid in Xero sets its pay lines to
// Processed and locks the shifts. Backend: routes/contractorBills.js, lib/contractorBills.js.

const EMPTY = [];
const ROW_STATUS = {
  ready: ['Ready to bill', 'badge-green'],
  held: ['Held back', 'badge-red'],
  billed: ['Billed', 'badge-purple'],
  nothing: ['Nothing to bill', 'badge-gray'],
};
const XERO_STATUS = {
  DRAFT: ['Draft in Xero', 'badge-blue'],
  SUBMITTED: ['Awaiting approval in Xero', 'badge-blue'],
  AUTHORISED: ['Approved in Xero', 'badge-purple'],
  PAID: ['Paid in Xero', 'badge-green'],
  VOIDED: ['Voided in Xero', 'badge-red'],
  DELETED: ['Deleted in Xero', 'badge-red'],
};
const BATCH_STATUS = { generated: ['Waiting to be marked paid', 'badge-blue'], paid: ['Paid', 'badge-green'], void: ['Void', 'badge-gray'] };
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function Pill({ pair }) {
  const [label, cls] = pair || ['Unknown', 'badge-gray'];
  return <span className={clsx(cls, 'whitespace-nowrap')}>{label}</span>;
}
const xeroPill = (b) => (b && b.xero_bill_id ? <Pill pair={XERO_STATUS[String(b.xero_status || '').toUpperCase()] || [b.xero_status || 'In Xero', 'badge-blue']} /> : <Pill pair={['Not in Xero yet', 'badge-gray']} />);

// A file from the API. A blob response hides the JSON error, so read it back as text.
async function download(url, fallbackName) {
  try {
    const r = await api.get(url, { responseType: 'blob' });
    const cd = r.headers && r.headers['content-disposition'];
    const name = (cd && /filename="?([^";]+)"?/.exec(cd)) ? /filename="?([^";]+)"?/.exec(cd)[1] : fallbackName;
    const href = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = href; a.download = name; a.click();
    URL.revokeObjectURL(href);
    return name;
  } catch (e) {
    const data = e && e.response && e.response.data;
    if (data instanceof Blob) {
      const text = await data.text().catch(() => '');
      let msg = '';
      try { msg = JSON.parse(text).message; } catch { msg = ''; }
      throw new Error(msg || 'The file could not be downloaded.');
    }
    throw e;
  }
}
const errOf = (e, fallback) => errText(e, (e && e.message) || fallback);

function Lines({ lines, subtotal, gst, total }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] text-[13px]">
        <thead>
          <tr className="border-b border-slate-200 text-[12px] font-semibold text-slate-500">
            <th scope="col" className="py-2 pr-3 text-left">Description</th>
            <th scope="col" className="px-3 py-2 text-right">Quantity</th>
            <th scope="col" className="px-3 py-2 text-right">Unit price</th>
            <th scope="col" className="px-3 py-2 text-right">Amount</th>
            <th scope="col" className="py-2 pl-3 text-right">GST</th>
          </tr>
        </thead>
        <tbody>
          {(lines || EMPTY).map((l, i) => (
            <tr key={`${l.timesheet_id || l.adjustment_id || 'x'}-${l.kind}-${i}`} className="border-b border-slate-100 last:border-0">
              <td className={clsx('py-2 pr-3', l.kind === 'adjustment' ? 'text-amber-800' : 'text-slate-800')}>{l.description}</td>
              <td className="px-3 py-2 text-right tabular-nums">{Number(l.quantity).toFixed(2)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{money(l.unit_amount)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{money(l.amount)}</td>
              <td className="py-2 pl-3 text-right tabular-nums">{money(l.gst || 0)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-semibold text-slate-900">
            <td colSpan={3} className="pt-2 pr-3 text-left">Subtotal{gst ? ` and GST ${money(gst)}` : ''}</td>
            <td className="px-3 pt-2 text-right tabular-nums">{money(subtotal)}</td>
            <td className="pl-3 pt-2 text-right tabular-nums">{money(gst || 0)}</td>
          </tr>
        </tfoot>
      </table>
      <p className="mt-3 text-right text-[15px] font-semibold tabular-nums text-slate-900">Total {money(total)}</p>
    </div>
  );
}

function Section({ n, title, sub, right, children }) {
  return (
    <section aria-labelledby={`cb-step-${n}`} className="rounded-2xl border border-slate-200/80 bg-white">
      <header className="flex flex-wrap items-start justify-between gap-3 px-5 pb-3 pt-4">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-[13px] font-bold text-violet-700" aria-hidden="true">{n}</span>
          <div className="min-w-0">
            <h2 id={`cb-step-${n}`} className="text-[15px] font-semibold leading-6 text-slate-900">{title}</h2>
            {sub && <p className="mt-0.5 text-[12.5px] text-slate-500">{sub}</p>}
          </div>
        </div>
        {right}
      </header>
      <div className="px-5 pb-5">{children}</div>
    </section>
  );
}

export default function ContractorBillsTab() {
  const confirm = useConfirm();
  const [periodStart, setPeriodStart] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState(null);
  const [scope, setScope] = useState('period');
  const [batches, setBatches] = useState(null);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const refreshed = useRef(false);

  const load = useCallback(async (start) => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await api.get('/contractor-bills/period', { params: start ? { period_start: start } : {} });
      setData(d);
      if (!start) setPeriodStart(d.period.start);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  const loadBills = useCallback(async () => {
    try {
      const { data: b } = await api.get('/contractor-bills/bills', { params: scope === 'period' && periodStart ? { period_start: periodStart } : {} });
      setBills(b);
    } catch { setBills(EMPTY); }
  }, [scope, periodStart]);
  const loadBatches = useCallback(async () => {
    try { const { data: b } = await api.get('/contractor-bills/batches'); setBatches(b); } catch { setBatches(EMPTY); }
  }, []);
  const reloadAll = useCallback(() => { load(periodStart); loadBills(); loadBatches(); }, [load, loadBills, loadBatches, periodStart]);

  useEffect(() => { load(''); loadBatches(); }, [load, loadBatches]);
  useEffect(() => { if (periodStart) loadBills(); }, [periodStart, scope, loadBills]);

  // Opening the screen reads the bills' status back from Xero (spec 8.3), once.
  const refresh = useCallback(async (quiet) => {
    setBusy('refresh');
    try {
      const { data: r } = await api.post('/contractor-bills/refresh');
      if (!quiet) {
        const bits = [`${plural(r.checked, 'bill')} checked`];
        if (r.processed) bits.push(`${r.processed} paid in Xero`);
        if (r.merged || r.updated) bits.push(`${(r.merged || 0) + (r.updated || 0)} updated in Xero`);
        toast.success(bits.join(', '));
      }
      reloadAll();
    } catch (e) { if (!quiet) toast.error(errOf(e, 'Could not read the bills from Xero.')); } finally { setBusy(''); }
  }, [reloadAll]);
  useEffect(() => {
    if (!data || refreshed.current || !data.xero_connected) return;
    refreshed.current = true;
    refresh(true);
  }, [data, refresh]);

  const rows = (data && data.contractors) || EMPTY;
  const ready = rows.filter((r) => r.status === 'ready');
  const held = rows.filter((r) => r.status === 'held');
  const blocked = data && (data.settings_problems.length > 0 || !data.xero_connected);

  const run = async () => {
    if (!ready.length) return;
    const ok = await confirm(`Send ${plural(ready.length, 'bill')} to Xero for ${data.period.label.replace('–', ' to ')}? ${ready.map((r) => `${r.name} ${money(r.preview.total)}`).join(', ')}.`,
      { title: 'Send contractor bills to Xero?', confirmLabel: `Send ${plural(ready.length, 'bill')}` });
    if (!ok) return;
    setBusy('run');
    try {
      const { data: r } = await api.post('/contractor-bills/run', { period_start: periodStart });
      setResult(r);
      reloadAll();
    } catch (e) { toast.error(errOf(e, 'Could not send the bills.')); } finally { setBusy(''); }
  };

  const changePeriod = (v) => { setPeriodStart(v); setPicked(new Set()); load(v); };

  if (error && !data) {
    const code = error.response && error.response.data && error.response.data.code;
    if (code === 'AWARD_ENGINE') {
      return <EmptyState icon={Receipt} title="Contractor bills come with Pay Rates" hint="This company pays from SCHADS pay groups. Contractors are paid from Financials > Pay contractors." />;
    }
    return <ErrorState message={errOf(error, "We couldn't load the contractor bills.")} onRetry={() => load(periodStart)} />;
  }
  if (!data) return <TableSkeleton rows={4} cols={5} />;

  const billList = bills || EMPTY;
  const payable = billList.filter((b) => b.payable);
  const togglePick = (id, on) => setPicked((p) => { const n = new Set(p); if (on) n.add(id); else n.delete(id); return n; });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <label htmlFor="cb-period" className="input-label">Pay period</label>
          <select id="cb-period" className="input-field min-w-[230px]" value={periodStart} onChange={(e) => changePeriod(e.target.value)}>
            {(data.periods || EMPTY).map((p) => (
              <option key={p.start} value={p.start}>{dmy(p.start)} to {dmy(p.end)}{p.current ? ' (current)' : ''}</option>
            ))}
            {!(data.periods || EMPTY).some((p) => p.start === periodStart) && <option value={periodStart}>{dmy(data.period.start)} to {dmy(data.period.end)}</option>}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {loading && <Loader2 size={15} className="animate-spin text-slate-400" aria-label="Loading" />}
          <button type="button" className="btn-secondary btn-sm" disabled={busy === 'refresh' || !data.xero_connected} onClick={() => refresh(false)}>
            {busy === 'refresh' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <RefreshCcw size={13} aria-hidden="true" />}Refresh from Xero
          </button>
          <Link to="/settings#sec-integrations" className="btn-ghost btn-sm"><Settings2 size={13} aria-hidden="true" />Bill settings</Link>
        </div>
      </div>

      {!data.xero_connected && (
        <Notice tone="amber" title="Xero is not connected" actions={<Link to="/settings#sec-integrations" className="btn-secondary btn-sm">Open Integrations</Link>}>
          Connect Xero in Settings &gt; Integrations to send contractor bills.
        </Notice>
      )}
      {data.settings_problems.length > 0 && (
        <Notice tone="amber" title="Finish the contractor bill settings" actions={<Link to="/settings#sec-integrations" className="btn-secondary btn-sm">Open Contractor Bills settings</Link>}>
          <ul className="list-disc space-y-0.5 pl-4">{data.settings_problems.map((p) => <li key={p}>{p.replace('Contractor bill settings: ', '')}</li>)}</ul>
        </Notice>
      )}
      {data.pending_changes > 0 && (
        <Notice tone="slate" title={`${plural(data.pending_changes, 'shift')} changed after billing`}>
          The bills are still Draft in Xero, so Refresh from Xero updates them. A bill that has been approved or paid gets the difference on the contractor&apos;s next bill instead.
        </Notice>
      )}

      <Section n={1} title="Contractor bills"
        sub="One bill per contractor for the pay period: one line per shift, a separate km line, sleepovers, and any adjustment from an earlier bill."
        right={(
          <button type="button" className="btn-primary btn-sm" disabled={!ready.length || blocked || busy === 'run'} onClick={run}
            title={blocked ? 'Connect Xero and finish the bill settings first.' : (!ready.length ? 'No contractor is ready to bill.' : undefined)}>
            {busy === 'run' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Send size={13} aria-hidden="true" />}
            Send {ready.length ? plural(ready.length, 'bill') : 'bills'} to Xero
          </button>
        )}>
        {rows.length === 0 ? (
          <p className="py-3 text-[13px] text-slate-500">No contractors. Set a worker&apos;s type to Contractor on their profile.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[12px] font-semibold text-slate-500">
                  <th scope="col" className="py-2.5 pr-3 text-left">Contractor</th>
                  <th scope="col" className="px-3 py-2.5 text-right">Shifts</th>
                  <th scope="col" className="px-3 py-2.5 text-left">Status</th>
                  <th scope="col" className="px-3 py-2.5 text-right">Amount</th>
                  <th scope="col" className="py-2.5 pl-3 text-left">Xero</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const amount = r.bill ? r.bill.total : r.preview.total;
                  return (
                    <tr key={r.user_id} className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50" onClick={() => setOpen(r)}>
                      <td className="py-2.5 pr-3">
                        <button type="button" className="font-medium text-slate-900 hover:text-primary-700 hover:underline" onClick={(e) => { e.stopPropagation(); setOpen(r); }}>{r.name}</button>
                        {r.gst_registered && <span className="ml-2 badge-gray">GST registered</span>}
                        {r.adjustments.length > 0 && <span className="ml-2 badge-yellow">{plural(r.adjustments.length, 'adjustment')}</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{r.bill ? (r.bill.lines || EMPTY).filter((l) => l.kind !== 'km' && l.kind !== 'adjustment').length : r.shifts}</td>
                      <td className="px-3 py-2.5"><Pill pair={ROW_STATUS[r.status]} /></td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{amount ? money(amount) : 'None'}</td>
                      <td className="py-2.5 pl-3">{r.bill ? xeroPill(r.bill) : <span className="text-[12.5px] text-slate-400">Not sent</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {held.length > 0 && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-semibold text-rose-900"><AlertTriangle size={15} aria-hidden="true" />Exceptions: {plural(held.length, 'contractor')} held back</p>
            <p className="mt-0.5 text-[12.5px] text-rose-800">They get no bill and no payment until this is fixed. Everyone else carries on.</p>
            <ul className="mt-2 space-y-2">
              {held.map((r) => (
                <li key={r.user_id} className="text-[13px]">
                  <Link to={`/staff/${r.user_id}#pay-rates`} className="font-semibold text-rose-900 hover:underline">{r.name}</Link>
                  <ul className="mt-0.5 space-y-0.5 pl-4">
                    {r.problems.map((p) => <li key={p.code} className="list-disc text-rose-800">{p.message}{p.fix ? <span className="text-rose-700"> Fixed by {p.fix}</span> : null}</li>)}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-[12.5px]" role="group" aria-label="Which bills">
              {[['period', 'This pay period'], ['all', 'All bills']].map(([v, l]) => (
                <button key={v} type="button" aria-pressed={scope === v} onClick={() => setScope(v)}
                  className={clsx('rounded-md px-2.5 py-1 font-medium', scope === v ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50')}>{l}</button>
              ))}
            </div>
            <p className="text-[12.5px] tabular-nums text-slate-500">{plural(billList.length, 'bill')} · {money(billList.reduce((t, b) => t + b.total, 0))}</p>
          </div>
          {bills === null ? <TableSkeleton rows={2} cols={6} /> : billList.length === 0 ? (
            <p className="py-2 text-[13px] text-slate-500">{scope === 'period' ? 'No bills for this pay period yet.' : 'No bills yet.'}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[12px] font-semibold text-slate-500">
                    <th scope="col" className="w-8 py-2.5"><span className="sr-only">Pay</span></th>
                    <th scope="col" className="py-2.5 pr-3 text-left">Bill</th>
                    <th scope="col" className="px-3 py-2.5 text-left">Contractor</th>
                    <th scope="col" className="px-3 py-2.5 text-left">Pay period</th>
                    <th scope="col" className="px-3 py-2.5 text-right">Total</th>
                    <th scope="col" className="px-3 py-2.5 text-left">Xero</th>
                    <th scope="col" className="py-2.5 pl-3 text-left">Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {billList.map((b) => (
                    <tr key={b.id} className="border-b border-slate-100 last:border-0">
                      <td className="py-2.5">
                        {b.payable && (
                          <input type="checkbox" className="rounded border-slate-300 text-primary-600" aria-label={`Pay ${b.bill_number}`}
                            checked={picked.has(b.id)} onChange={(e) => togglePick(b.id, e.target.checked)} />
                        )}
                      </td>
                      <td className="py-2.5 pr-3">
                        <button type="button" className="font-mono text-[12.5px] text-primary-700 hover:underline" onClick={() => setOpen({ name: b.name, bill: b })}>{b.bill_number}</button>
                        {b.xero_error && <span className="mt-0.5 block text-[11.5px] text-rose-700">{b.xero_error}</span>}
                      </td>
                      <td className="px-3 py-2.5">{b.name}</td>
                      <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-slate-600">{dmy(b.period_start)} to {dmy(b.period_end)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums"><span className="font-semibold text-slate-900">{money(b.total)}</span>{b.gst ? <span className="block text-[11.5px] text-slate-500">incl. {money(b.gst)} GST</span> : null}</td>
                      <td className="px-3 py-2.5">{xeroPill(b)}</td>
                      <td className="py-2.5 pl-3 text-[12.5px]">
                        {b.aba_paid_at ? <span className="text-emerald-700">Paid {dmy(b.aba_paid_at)}</span>
                          : b.batch_ref ? <span className="font-mono text-sky-700">{b.batch_ref}</span>
                            : !b.has_bank ? <span className="badge-red">No bank details</span>
                              : b.bank_needs_confirmation ? <span className="badge-yellow">Bank change to confirm</span>
                                : <span className="text-slate-500">Not paid</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {payable.length > 0 && <p className="mt-2 text-[12px] text-slate-500">Tick the bills to pay, then make the payment file below.</p>}
        </div>
      </Section>

      <PaymentFile bills={billList} picked={picked} setPicked={setPicked} batches={batches} abaProblems={data.aba_problems} onChanged={reloadAll} />

      <BillDialog row={open} onClose={() => setOpen(null)} />
      <ResultDialog result={result} onClose={() => setResult(null)} />
    </div>
  );
}

function BillDialog({ row, onClose }) {
  const b = row && row.bill;
  const p = row && !b ? row.preview : null;
  return (
    <Dialog open={!!row} onClose={onClose} size="lg"
      title={b ? `${b.bill_number} · ${row.name}` : (row ? `${row.name}: bill preview` : '')}
      description={b ? `${dmy(b.period_start)} to ${dmy(b.period_end)} · due ${dmy(b.due_date)}` : 'What the bill would hold if it were sent now.'}>
      {row && (
        <div className="space-y-4">
          {b && (
            <div className="flex flex-wrap items-center gap-2">
              {xeroPill(b)}
              {b.gst_registered && <span className="badge-gray">GST registered</span>}
              {b.needs_update && <span className="badge-yellow">Waiting to update in Xero</span>}
              {b.processed_at && <span className="badge-green">Pay lines processed {dmy(b.processed_at)}</span>}
            </div>
          )}
          {!b && row.problems && row.problems.length > 0 && (
            <Notice tone="rose" title="Held back">
              <ul className="list-disc space-y-0.5 pl-4">{row.problems.map((x) => <li key={x.code}>{x.message}</li>)}</ul>
            </Notice>
          )}
          {(b ? b.lines : p.lines).length ? (
            <Lines lines={b ? b.lines : p.lines} subtotal={b ? b.subtotal : p.subtotal} gst={b ? b.gst : p.gst} total={b ? b.total : p.total} />
          ) : <p className="text-[13px] text-slate-500">No approved pay lines to bill.</p>}
        </div>
      )}
    </Dialog>
  );
}

function ResultDialog({ result, onClose }) {
  const made = ((result && result.results) || EMPTY).filter((x) => x.ok);
  const failed = ((result && result.results) || EMPTY).filter((x) => !x.ok);
  const held = (result && result.exceptions) || EMPTY;
  return (
    <Dialog open={!!result} onClose={onClose} size="md" title={made.length ? `${plural(made.length, 'bill')} sent to Xero` : 'No bills sent'}
      description={result ? `${dmy(result.period.start)} to ${dmy(result.period.end)}. Approve the bills in Xero, then pay them with a payment file.` : ''}
      footer={<button type="button" className="btn-primary" onClick={onClose}>Done</button>}>
      <div className="space-y-4 text-[13px]">
        {made.length > 0 && (
          <div>
            <p className="flex items-center gap-1.5 font-semibold text-emerald-800"><CheckCircle2 size={14} aria-hidden="true" />Sent</p>
            <ul className="mt-1 space-y-1">{made.map((x) => (
              <li key={x.user_id} className="flex flex-wrap justify-between gap-2">
                <span><span className="font-mono">{x.bill.bill_number}</span> · {x.name}{x.bill.warning ? <span className="block text-amber-800">{x.bill.warning}</span> : null}</span>
                <span className="font-semibold tabular-nums">{money(x.bill.total)}</span>
              </li>
            ))}</ul>
          </div>
        )}
        {failed.length > 0 && (
          <div>
            <p className="font-semibold text-rose-800">Not sent</p>
            <ul className="mt-1 space-y-1">{failed.map((x) => <li key={x.user_id}><span className="font-medium">{x.name}</span>: {x.error}</li>)}</ul>
          </div>
        )}
        {held.length > 0 && (
          <div>
            <p className="font-semibold text-slate-800">Held back (Exceptions)</p>
            <ul className="mt-1 space-y-1">{held.map((x) => <li key={x.user_id}><span className="font-medium">{x.name}</span>: {x.problems.map((p) => p.message).join(' ')}</li>)}</ul>
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ── Step 2: the payment file (ABA), as the SC module ─────────────────────────────────────────────
function PaymentFile({ bills, picked, setPicked, batches, abaProblems, onChanged }) {
  const confirm = useConfirm();
  const [date, setDate] = useState(todayIso);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [made, setMade] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [reason, setReason] = useState('');
  const ids = useMemo(() => bills.filter((b) => b.payable && picked.has(b.id)).map((b) => b.id), [bills, picked]);
  useEffect(() => { setPreview(null); }, [ids.length, date]);

  const check = async () => {
    setBusy('check');
    try { const { data } = await api.post('/contractor-bills/aba/preview', { bill_ids: ids, payment_date: date }); setPreview(data); }
    catch (e) { toast.error(errOf(e, 'Could not check these payments.')); } finally { setBusy(''); }
  };
  const generate = async (dupOk) => {
    setBusy('gen');
    try {
      const { data } = await api.post('/contractor-bills/aba/generate', { bill_ids: ids, payment_date: date, ...(dupOk ? { confirm: true } : {}) });
      setMade(data); setPreview(null); setPicked(new Set()); onChanged();
      toast.success(`Payment file ${data.batch.batch_ref} is ready to download`);
    } catch (e) {
      const x = (e && e.response && e.response.data) || {};
      if (x.code === 'POSSIBLE_DUPLICATE' && await confirm(x.message, { title: 'This looks like a repeat payment', confirmLabel: 'Generate anyway', danger: true })) { setBusy(''); generate(true); return; }
      if (Array.isArray(x.problems)) setPreview((p) => (p ? { ...p, problems: x.problems, can_generate: false } : p));
      toast.error(errOf(e, 'Could not generate the ABA file.'));
    } finally { setBusy(''); }
  };
  const fetchFile = async (b) => {
    try { const name = await download(`/contractor-bills/batches/${b.id}/file`, `${b.batch_ref}.aba`); toast.success(`Downloaded ${name}. Upload it at your bank.`); }
    catch (e) { toast.error(errOf(e, 'Could not download the payment file.')); }
  };
  const markPaid = async (b) => {
    const ok = await confirm(`Mark ${b.batch_ref} as paid (${plural(b.payment_count, 'payment')}, ${money(b.total)})? Only do this once the bank shows the payments went out. Remittance advice is emailed to each contractor.`,
      { title: 'Mark the payment file as paid?', confirmLabel: 'Mark paid' });
    if (!ok) return;
    setBusy(`paid-${b.id}`);
    try {
      const { data } = await api.post(`/contractor-bills/batches/${b.id}/paid`, { send_remittance: true });
      const sent = data.remittance ? data.remittance.sent : 0;
      toast.success(`${b.batch_ref} marked paid. ${plural(sent, 'remittance advice', 'remittance advices')} emailed. Record the payment against the bills in Xero.`);
      onChanged();
    } catch (e) { toast.error(errOf(e, 'Could not mark the batch as paid.')); } finally { setBusy(''); }
  };
  const doVoid = async () => {
    if (!reason.trim()) return;
    setBusy('void');
    try {
      const { data } = await api.post(`/contractor-bills/batches/${voiding.id}/void`, { reason });
      toast.success(`${voiding.batch_ref} voided. ${plural(data.bills_released || 0, 'bill')} can be paid again.`);
      setVoiding(null); setReason(''); if (made && made.batch.id === voiding.id) setMade(null); onChanged();
    } catch (e) { toast.error(errOf(e, 'Could not void the batch.')); } finally { setBusy(''); }
  };

  const list = batches || EMPTY;
  return (
    <Section n={2} title="Payment file"
      sub="An ABA file pays contractor bills in Xero from your bank. Download it here and upload it at your bank; it is never emailed.">
      {abaProblems && abaProblems.length > 0 && (
        <Notice tone="amber" className="mb-4" title="The ABA settings are not finished" actions={<Link to="/settings#sec-integrations" className="btn-secondary btn-sm">Open Contractor Bills settings</Link>}>
          <ul className="list-disc space-y-0.5 pl-4">{abaProblems.map((p) => <li key={p}>{p.replace('ABA settings: ', '')}</li>)}</ul>
        </Notice>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <p className="min-w-[180px] flex-1 text-[13px] text-slate-700">
          {ids.length ? <><span className="font-semibold">{plural(ids.length, 'bill')}</span> chosen · {money(bills.filter((b) => ids.includes(b.id)).reduce((t, b) => t + b.total, 0))}</> : 'Tick the bills to pay in the list above.'}
        </p>
        <div>
          <label htmlFor="cb-pay-date" className="input-label">Payment date</label>
          <input id="cb-pay-date" type="date" className="input-field" min={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <button type="button" className="btn-secondary" disabled={!ids.length || !date || busy === 'check'} onClick={check}>
          {busy === 'check' ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Landmark size={14} aria-hidden="true" />}Check payments
        </button>
      </div>

      {preview && (
        <div className="mt-4 space-y-3 rounded-xl border border-slate-200 p-4">
          <p className="text-[13px] text-slate-700"><span className="font-semibold">{plural(preview.payment_count, 'payment')}</span> on {dmy(preview.payment_date)} · <span className="font-semibold tabular-nums">{money(preview.total)}</span></p>
          {preview.problems.length > 0 && (
            <Notice tone="rose" title="Fix these before generating the file">
              <ul className="list-disc space-y-0.5 pl-4">{preview.problems.map((p) => <li key={p}>{p}</li>)}</ul>
            </Notice>
          )}
          <ul className="divide-y divide-slate-100 text-[13px]">
            {preview.payments.map((p) => (
              <li key={p.bill_id} className="flex flex-wrap justify-between gap-2 py-1.5">
                <span>{p.name} <span className="text-slate-500">· {p.bsb} {p.account_masked} · ref {p.lodgement_ref}</span></span>
                <span className="font-semibold tabular-nums">{money(p.amount)}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end">
            <button type="button" className="btn-primary" disabled={!preview.can_generate || busy === 'gen'} onClick={() => generate(false)}>
              {busy === 'gen' ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <FileText size={14} aria-hidden="true" />}Generate ABA file
            </button>
          </div>
        </div>
      )}

      {made && (
        <Notice tone="green" className="mt-4" title={`Payment file ${made.batch.batch_ref} is ready`}
          actions={<button type="button" className="btn-secondary btn-sm" onClick={() => fetchFile(made.batch)}><Download size={13} aria-hidden="true" />Download ABA file</button>}>
          {made.next_step}
        </Notice>
      )}

      <div className="mt-5 border-t border-slate-100 pt-4">
        <h3 className="mb-2 text-[13px] font-semibold text-slate-800">Payment files</h3>
        {batches === null ? <TableSkeleton rows={2} cols={5} /> : list.length === 0 ? <p className="text-[13px] text-slate-500">No payment files yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[12px] font-semibold text-slate-500">
                  <th scope="col" className="py-2 pr-3 text-left">Batch</th>
                  <th scope="col" className="px-3 py-2 text-left">Payment date</th>
                  <th scope="col" className="px-3 py-2 text-right">Payments</th>
                  <th scope="col" className="px-3 py-2 text-right">Total</th>
                  <th scope="col" className="px-3 py-2 text-left">Status</th>
                  <th scope="col" className="py-2 pl-3 text-right"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {list.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-3 font-mono text-[12.5px]">{b.batch_ref}</td>
                    <td className="px-3 py-2 tabular-nums">{dmy(b.payment_date)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{b.payment_count}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{money(b.total)}</td>
                    <td className="px-3 py-2"><Pill pair={BATCH_STATUS[b.status]} />{b.void_reason ? <span className="block text-[11.5px] text-slate-500">{b.void_reason}</span> : null}</td>
                    <td className="py-2 pl-3">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {b.status !== 'void' && <button type="button" className="btn-ghost btn-sm" onClick={() => fetchFile(b)}><Download size={13} aria-hidden="true" />File</button>}
                        {b.status === 'generated' && <button type="button" className="btn-secondary btn-sm" disabled={busy === `paid-${b.id}`} onClick={() => markPaid(b)}><CheckCircle2 size={13} aria-hidden="true" />Mark paid</button>}
                        {b.status === 'generated' && <button type="button" className="btn-ghost btn-sm text-rose-700" onClick={() => { setVoiding(b); setReason(''); }}><Ban size={13} aria-hidden="true" />Void</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!voiding} onClose={() => setVoiding(null)} size="sm" title={voiding ? `Void ${voiding.batch_ref}?` : ''}
        description="Only void a file the bank has not paid. Its bills can then go in a new payment file."
        footer={(
          <>
            <button type="button" className="btn-secondary" onClick={() => setVoiding(null)}>Cancel</button>
            <button type="button" className="btn-danger" disabled={!reason.trim() || busy === 'void'} onClick={doVoid}>Void the file</button>
          </>
        )}>
        <label htmlFor="cb-void-reason" className="input-label">Reason</label>
        <textarea id="cb-void-reason" rows={3} className="input-field" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="For example: the wrong payment date" />
      </Dialog>
    </Section>
  );
}
